import { SafeExternalLink } from "@/components/SafeExternalLink";
import { PaywallBadge } from "@/components/PaywallBadge";
import { OutboundArrow } from "@/components/OutboundArrow";
import { TOPIC_LABEL, type Topic } from "@/lib/topics";
import { renderWithWikiLinks } from "@/lib/wiki-link";
import { normalizeTitle } from "@/lib/normalize-title";

type Article = {
  id: string;
  original_url: string;
  title: string;
  summary?: string | null;
  selection_reason?: string | null;
  source: string;
  topic: string;
  paywall: boolean;
  image_url?: string | null;
};

/**
 * Ugens fund – én fremhævet tekst som helten på forsiden.
 * To-spaltet på desktop (titel/manchet | derfor-boks + link),
 * stables på mobil. Bygger på øverste fund fra fresh-strejf, så vi
 * ikke duplikerer et separat indhold.
 */
function prettySourceName(source: string, url: string): string {
  if (source && !/^https?:\/\//i.test(source)) return source;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return source || "kilden";
  }
}

export function WeeklyFund({
  article,
  onOpen,
  eyebrow,
  readLabel,
  reasonLabel,
}: {
  article: Article;
  onOpen?: () => void;
  eyebrow?: string;
  readLabel?: string;
  reasonLabel?: string;
}) {
  const kicker = eyebrow ?? `Ugens fund · ${TOPIC_LABEL[article.topic as Topic] ?? "essay"}`;
  const manchet = article.summary?.trim() || null;
  const sourceName = prettySourceName(article.source, article.original_url);
  const derfor =
    article.selection_reason?.trim() ||
    "Et fund jeg har læst med opmærksomhed – og vil gerne dele videre.";

  return (
    <section
      className="rule-double py-12 md:py-[52px]"
      aria-label="Ugens fund"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-6 md:mb-7">
        <span className="eyebrow">{kicker}</span>
        <span className="meta not-italic font-serif text-[13px] tracking-normal">
          <PaywallBadge paywall={article.paywall} />
          <span className="ml-2">{sourceName}</span>
        </span>
      </div>

      <div className="grid gap-8 md:gap-14 md:grid-cols-[1.5fr_1fr] md:items-end">
        <div>
          <h2
            className="font-serif font-normal text-ink m-0 mb-3 md:mb-5 leading-[1.03] tracking-[-0.01em]"
            style={{ fontSize: "clamp(32px, 5.4vw, 58px)" }}
          >
            {normalizeTitle(article.title)}
          </h2>
          {manchet && (
            <p className="font-serif italic font-light text-[18px] md:text-[21px] leading-[1.45] text-ink-soft m-0">
              {renderWithWikiLinks(manchet)}
            </p>
          )}
        </div>

        <div>
          <p className="eyebrow mb-2.5">{reasonLabel ?? "Derfor har jeg valgt det"}</p>
          <p className="font-serif font-light text-[15px] md:text-[16px] leading-[1.72] text-ink-body m-0 mb-5">
            {renderWithWikiLinks(derfor)}
          </p>
          <SafeExternalLink
            href={article.original_url}
            onOpen={onOpen}
            className="inline-block font-serif italic text-[16px] text-accent border-b border-accent pb-[3px] hover:text-accent-dark hover:border-accent-dark transition-colors"
          >
            {readLabel ?? "Læs hos "}<span className="whitespace-nowrap">{sourceName}<OutboundArrow /></span>
          </SafeExternalLink>
        </div>
      </div>
    </section>
  );
}
